/* Plant Finder - quiz scoring and rendering.
 *
 * Attribute data is loaded from the plant-finder-data.json asset, generated by
 * _plantfinder/build-plantfinder-data.js. Products absent from that file are not
 * eligible for the quiz. That is how unconfirmed pet-safety products stay excluded.
 *
 * All customer-facing copy comes from section settings, passed in as data
 * attributes on the root element. Nothing here should be hard-coded English.
 *
 * To move to metafields later: replace loadData() with a Liquid-rendered payload
 * built from product.metafields.plantfinder.*. Nothing else needs to change.
 */
(function () {
  'use strict';

  var WEIGHTS = { light: 30, care: 25, size: 25, room: 20 };

  var COPY = {
    light: {
      shade:   'would prefer a dimmer spot',
      partial: 'would rather have gentler, indirect light',
      sun:     'would prefer a brighter, sunnier spot'
    },
    size: 'is not {a} {x} plant',
    care: 'asks for a slightly greener thumb',
    room: "isn't the most natural fit for {x}"
  };

  var ROOM_LABEL = {
    living: 'a living room', bedroom: 'a bedroom',
    bathroom: 'a bathroom', office: 'an office'
  };
  var SIZE_LABEL = { xs: 'extra small', s: 'small', m: 'medium', l: 'large', xl: 'extra large' };

  // Results below this failed nearly everything asked for and are not worth showing.
  var MIN_SCORE = 40;

  function article(word) {
    return /^[aeiou]/i.test(word) ? 'an' : 'a';
  }

  /* Trust feature columns shown right after the top-three podium. The markup and
     icon images live in a hidden <template id="pf-features-template"> rendered by
     sections/plant-finder.liquid, so the icons use the real theme image URLs and
     the grey-card styling matches the homepage feature row exactly. */
  function getFeaturesHTML() {
    var tpl = document.getElementById('pf-features-template');
    return tpl ? tpl.innerHTML : '';
  }

  function PlantFinder(root) {
    this.root = root;
    this.answers = { room: '', light: '', size: '', care: '', pets: '' };
    this.products = [];
    this.page = 0;
    this.ranked = [];

    var d = root.dataset;
    this.cfg = {
      resultsTitle: d.resultsTitle || 'And the winners are...',
      moreTitle:    d.moreTitle    || 'But we have plenty more in store for you',
      moreText:     d.moreText     || '',
      perfectText:  d.perfectText  || 'A perfect match for you.',
      whyLabel:     d.whyLabel     || 'Why not 100%?',
      becauseLabel: d.becauseLabel || 'Well, because this plant:',
      matchLabel:   d.matchLabel   || 'match',
      emptyTitle:   d.emptyTitle   || 'No pet-safe matches for that combination just yet',
      emptyText:    d.emptyText    || '',
      showRank:     d.showRank === 'true',
      showMatch:    d.showMatch !== 'false',
      cols:         parseInt(d.cols, 10) || 4,
      rows:         parseInt(d.rows, 10) || 2,
      maxResults:   parseInt(d.maxResults, 10) || 48
    };

    this.form = root.querySelector('[data-pf-form]');
    this.resultsEl = root.querySelector('[data-pf-results]');
    this.submitBtn = root.querySelector('[data-pf-submit]');
    this.resetBtn = root.querySelector('[data-pf-reset]');
    this.bind();
    this.loadData();
  }

  PlantFinder.prototype.pageSize = function () {
    return Math.max(1, this.cfg.cols * this.cfg.rows);
  };

  PlantFinder.prototype.loadData = function () {
    var src = this.root.getAttribute('data-source');
    var self = this;
    fetch(src, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (d) {
        self.products = d.products || [];
        self.root.removeAttribute('data-pf-loading');
      })
      .catch(function () {
        self.root.setAttribute('data-pf-error', '');
        self.resultsEl.innerHTML =
          '<p class="plant-finder__error">We could not load the plant list just now. ' +
          'Please refresh the page, or <a href="/collections/all-plants">browse all plants</a>.</p>';
      });
  };

  /* ---------------------------------------------------------------- events */
  PlantFinder.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-pf-trigger]');
      var option = e.target.closest('[data-pf-option]');
      var page = e.target.closest('[data-pf-page]');

      if (trigger) { self.toggleDropdown(trigger.closest('[data-pf-field]')); return; }
      if (option)  { self.choose(option); return; }
      if (page)    { self.goToPage(parseInt(page.getAttribute('data-pf-page'), 10)); return; }
      self.closeAll();
    });

    // Clicking anywhere else on the page dismisses an open dropdown. Scoped so it
    // only ever closes this section's own menus and never touches other behaviour.
    document.addEventListener('click', function (e) {
      if (!self.root.contains(e.target)) self.closeAll();
    });

    this.root.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { self.closeAll(); return; }
      var option = e.target.closest('[data-pf-option]');
      if (option && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); self.choose(option); }
      var trigger = e.target.closest('[data-pf-trigger]');
      if (trigger && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        self.toggleDropdown(trigger.closest('[data-pf-field]'));
      }
    });

    if (this.submitBtn) this.submitBtn.addEventListener('click', function () { self.showResults(); });
    if (this.resetBtn)  this.resetBtn.addEventListener('click', function () { self.reset(); });
  };

  PlantFinder.prototype.toggleDropdown = function (field) {
    if (!field) return;
    var open = field.hasAttribute('data-open');
    this.closeAll();
    if (!open) {
      field.setAttribute('data-open', '');
      field.querySelector('[data-pf-trigger]').setAttribute('aria-expanded', 'true');
    }
  };

  PlantFinder.prototype.closeAll = function () {
    this.root.querySelectorAll('[data-pf-field][data-open]').forEach(function (f) {
      f.removeAttribute('data-open');
      f.querySelector('[data-pf-trigger]').setAttribute('aria-expanded', 'false');
    });
  };

  PlantFinder.prototype.choose = function (option) {
    var field = option.closest('[data-pf-field]');
    this.answers[field.getAttribute('data-pf-field')] = option.getAttribute('data-value');

    var trigger = field.querySelector('[data-pf-trigger]');
    trigger.textContent = option.getAttribute('data-label') || option.textContent.trim();
    trigger.classList.add('is-answered');

    field.querySelectorAll('[data-pf-option]').forEach(function (o) {
      o.setAttribute('aria-selected', o === option ? 'true' : 'false');
    });

    this.closeAll();
    this.updateSubmitState();
  };

  PlantFinder.prototype.complete = function () {
    var a = this.answers;
    return a.room && a.light && a.size && a.care && a.pets;
  };

  PlantFinder.prototype.updateSubmitState = function () {
    if (!this.submitBtn) return;
    var ready = this.complete();
    this.submitBtn.disabled = !ready;
    this.submitBtn.setAttribute('aria-disabled', ready ? 'false' : 'true');
  };

  /* ---------------------------------------------------------------- scoring */
  PlantFinder.prototype.score = function (p) {
    var a = this.answers, pts = 0, misses = [];

    // Light. A plant matches if it tolerates the light the customer has.
    if (!a.light || (p.light || []).indexOf(a.light) !== -1) {
      pts += WEIGHTS.light;
    } else {
      misses.push(COPY.light[(p.light || [])[0]] || 'would prefer different light');
    }

    // Size. "Any" always matches.
    if (a.size === 'any' || !a.size || (p.size || []).indexOf(a.size) !== -1) {
      pts += WEIGHTS.size;
    } else {
      var sizeWord = SIZE_LABEL[a.size] || a.size;
      misses.push(COPY.size.replace('{a}', article(sizeWord)).replace('{x}', sizeWord));
    }

    // Care. A beginner should not be handed an expert plant. Experts can have anything.
    if (a.care === 'expert' || p.care !== 'expert') {
      pts += WEIGHTS.care;
    } else {
      misses.push(COPY.care);
    }

    // Room.
    if (a.room === 'unsure' || !a.room || (p.room || []).indexOf(a.room) !== -1) {
      pts += WEIGHTS.room;
    } else {
      misses.push(COPY.room.replace('{x}', ROOM_LABEL[a.room] || a.room));
    }

    // Specificity breaks ties between equally-scoring plants. A plant that tolerates
    // only the light you have is a better answer than one that tolerates everything.
    // Without this, roughly 135 plants tie at 100% and the podium is alphabetical.
    var spec = 0;
    if (a.light && (p.light || []).indexOf(a.light) !== -1) spec += 3 / (p.light.length || 1);
    if (a.size && a.size !== 'any' && (p.size || []).indexOf(a.size) !== -1) spec += 3 / (p.size.length || 1);
    if (a.room && a.room !== 'unsure' && (p.room || []).indexOf(a.room) !== -1) spec += 2 / (p.room.length || 1);
    if (a.care === 'easy' && p.care === 'easy') spec += 1;

    return { product: p, pct: Math.round(pts), misses: misses, spec: spec };
  };

  // Deterministic per-product ordering, so equal matches are not served alphabetically
  // (which otherwise clusters "Algerian Ivy Green / Neon" and 22 Calatheas together).
  function seed(s) {
    var h = 2166136261;
    for (var i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
    return (h >>> 0) / 4294967295;
  }

  function genusOf(title) {
    return String(title).split(/[\s|,-]+/)[0].toLowerCase();
  }

  PlantFinder.prototype.rank = function () {
    var self = this;
    var pool = this.products;

    // Pet safety is a hard filter, never a score. Anything not explicitly confirmed
    // safe is withheld when the customer tells us they have pets or small children.
    if (this.answers.pets === 'yes') {
      pool = pool.filter(function (p) { return p.pet === 'safe'; });
    }

    return pool
      .map(function (p) { return self.score(p); })
      .sort(function (x, y) {
        return y.pct - x.pct || y.spec - x.spec || seed(x.product.handle) - seed(y.product.handle);
      });
  };

  // The top three should feel like three different ideas, not three variants of one
  // plant. Take the best of each genus first, then fill any gap from what is left.
  PlantFinder.prototype.podium = function (ranked) {
    var picked = [], seen = {}, i;
    for (i = 0; i < ranked.length && picked.length < 3; i++) {
      var g = genusOf(ranked[i].product.title);
      if (seen[g]) continue;
      seen[g] = true;
      picked.push(ranked[i]);
    }
    for (i = 0; i < ranked.length && picked.length < 3; i++) {
      if (picked.indexOf(ranked[i]) === -1) picked.push(ranked[i]);
    }
    return picked;
  };

  /* ---------------------------------------------------------------- render */
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function money(p) {
    if (p.price == null) return '';
    try {
      return new Intl.NumberFormat('en-CA', { style: 'currency', currency: p.currency || 'CAD' }).format(p.price);
    } catch (e) { return '$' + p.price; }
  }

  PlantFinder.prototype.card = function (r, rankIndex) {
    var p = r.product;
    var cfg = this.cfg;
    var why = r.misses.length
      ? '<details class="pf-card__why">' +
          '<summary>' + esc(cfg.whyLabel) + '</summary>' +
          '<p>' + esc(cfg.becauseLabel) + '</p><ul><li>' +
          r.misses.map(esc).join('</li><li>') + '</li></ul>' +
        '</details>'
      : '<p class="pf-card__why pf-card__why--perfect">' + esc(cfg.perfectText) + '</p>';

    var rank = (rankIndex != null && cfg.showRank)
      ? '<span class="pf-card__rank" aria-hidden="true">' + (rankIndex + 1) + '</span>' : '';

    var match = cfg.showMatch
      ? '<span class="pf-card__match">' + r.pct + '% <small>' + esc(cfg.matchLabel) + '</small></span>' : '';

    return '' +
      '<li class="pf-card' + (rankIndex != null ? ' pf-card--podium' : '') + '">' +
        rank +
        '<a class="pf-card__link" href="' + esc(p.url) + '">' +
          '<span class="pf-card__media">' +
            (p.image
              ? '<img src="' + esc(p.image) + '" alt="' + esc(p.title) + '" loading="lazy" width="300" height="300">'
              : '<span class="pf-card__media--empty" aria-hidden="true"></span>') +
            match +
          '</span>' +
          '<span class="pf-card__title">' + esc(p.title) + '</span>' +
          '<span class="pf-card__price">' + esc(money(p)) + '</span>' +
        '</a>' +
        why +
      '</li>';
  };

  PlantFinder.prototype.pagination = function (totalPages) {
    if (totalPages < 2) return '';
    var cur = this.page, out = [], i;

    var btn = function (n, label, cls, disabled) {
      return '<button type="button" class="pf-page ' + (cls || '') + '"' +
             (disabled ? ' disabled' : ' data-pf-page="' + n + '"') +
             (n === cur && !cls ? ' aria-current="page"' : '') +
             '>' + label + '</button>';
    };

    out.push(btn(cur - 1, 'Previous', 'pf-page--nav', cur === 0));

    // window of pages around the current one, with ellipses
    var pages = [];
    for (i = 0; i < totalPages; i++) {
      if (i === 0 || i === totalPages - 1 || Math.abs(i - cur) <= 1) pages.push(i);
    }
    var last = -1;
    pages.forEach(function (n) {
      if (last !== -1 && n - last > 1) out.push('<span class="pf-page-gap">...</span>');
      out.push(btn(n, String(n + 1)));
      last = n;
    });

    out.push(btn(cur + 1, 'Next', 'pf-page--nav', cur === totalPages - 1));

    return '<nav class="pf-pagination" aria-label="More matches">' + out.join('') + '</nav>';
  };

  PlantFinder.prototype.goToPage = function (n) {
    var rest = this.ranked.rest || [];
    var total = Math.ceil(rest.length / this.pageSize());
    if (n < 0 || n >= total) return;
    this.page = n;
    this.paint();
    var grid = this.resultsEl.querySelector('.pf-grid');
    if (grid) grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  PlantFinder.prototype.paint = function () {
    var self = this;
    var podium = this.ranked.podium || [];
    var rest = this.ranked.rest || [];
    var cfg = this.cfg;

    if (!podium.length) {
      this.resultsEl.innerHTML =
        '<div class="plant-finder__empty">' +
          '<h2>' + esc(cfg.emptyTitle) + '</h2>' +
          (cfg.emptyText ? '<p>' + cfg.emptyText + '</p>' : '') +
        '</div>';
      return;
    }

    var size = this.pageSize();
    var totalPages = Math.ceil(rest.length / size);
    var slice = rest.slice(this.page * size, (this.page + 1) * size);

    this.resultsEl.innerHTML =
      '<div class="plant-finder__results-inner">' +
        (cfg.resultsTitle ? '<h2 class="plant-finder__results-title">' + esc(cfg.resultsTitle) + '</h2>' : '') +
        '<ol class="pf-podium">' +
          podium.map(function (r, i) { return self.card(r, i); }).join('') +
        '</ol>' +
        getFeaturesHTML() +
        (rest.length
          ? (cfg.moreTitle ? '<h3 class="plant-finder__more-title">' + esc(cfg.moreTitle) + '</h3>' : '') +
            (cfg.moreText ? '<div class="plant-finder__more-text rte">' + cfg.moreText + '</div>' : '') +
            '<ul class="pf-grid">' +
              slice.map(function (r) { return self.card(r, null); }).join('') +
            '</ul>' +
            this.pagination(totalPages)
          : '') +
      '</div>';
  };

  PlantFinder.prototype.showResults = function () {
    if (!this.complete()) return;

    var ranked = this.rank();

    // Drop the no-hopers. If a narrow search leaves fewer than three, keep the best
    // three anyway. An honest "closest we have" beats an empty page.
    var worth = ranked.filter(function (r) { return r.pct >= MIN_SCORE; });
    if (worth.length < 3) worth = ranked.slice(0, 3);

    var podium = this.podium(worth);
    // Cap the tail. Without this a broad search paginates to 50+ pages of 45%
    // matches, which is noise rather than choice.
    this.ranked = {
      podium: podium,
      rest: worth
        .filter(function (r) { return podium.indexOf(r) === -1; })
        .slice(0, this.cfg.maxResults)
    };
    this.page = 0;
    this.paint();

    this.root.setAttribute('data-pf-state', 'results');
    this.resultsEl.setAttribute('tabindex', '-1');
    this.resultsEl.focus({ preventScroll: true });
    this.resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  PlantFinder.prototype.reset = function () {
    this.answers = { room: '', light: '', size: '', care: '', pets: '' };
    this.ranked = [];
    this.page = 0;
    this.root.querySelectorAll('[data-pf-trigger]').forEach(function (t) {
      t.textContent = t.getAttribute('data-placeholder') || '';
      t.classList.remove('is-answered');
    });
    this.root.querySelectorAll('[data-pf-option]').forEach(function (o) {
      o.setAttribute('aria-selected', 'false');
    });
    this.resultsEl.innerHTML = '';
    this.root.removeAttribute('data-pf-state');
    this.updateSubmitState();
    this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  function init() {
    document.querySelectorAll('[data-plant-finder]').forEach(function (el) {
      if (!el.__pf) el.__pf = new PlantFinder(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();